/**
 * O CONTRATO DE CARREGADOR NA APRESENTAÇÃO — a ponte entre `@nxvolt/shared` e
 * as telas de pátio, em módulo PURO.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE ARQUIVO EXISTE
 *
 * Havia CINCO declarações locais divergentes da mesma linha de carregador
 * (`ChargersPage`, `ChargerDetailPage`, `GaragesTariffsPanel` e duas dentro de
 * `modules-extra`), e o custo já estava medido: `tsc --noEmit` passava limpo nos
 * quatro pacotes enquanto o front lia `lastHeartbeatAt` — campo que o servidor
 * NÃO publica — em três arquivos, e não tinha nenhuma ocorrência de
 * `serialNumber` nem de `contact`.
 *
 * O MECANISMO DA CEGUEIRA, por extenso, porque ele vai renascer:
 *
 *     .then((d: { chargers: Charger[] }) => setChargers(d.chargers))
 *
 * `r.json()` devolve `any`. A anotação sobre um `any` não é verificação, é
 * AFIRMAÇÃO — o compilador aceita qualquer nome de campo que o programador
 * escreva ali, e depois cobra coerência só contra a declaração LOCAL. Os dois
 * lados compilam contra a própria cópia do contrato e o encontro acontece no
 * navegador. Nesta mesma frente isso deixou o portal INTEIRO em branco
 * (`document.body.innerText.length === 0`), porque não há error boundary na
 * árvore toda.
 *
 * O CONSERTO É ESTRUTURAL E TEM DUAS METADES:
 *   1. a linha de carregador é `LinhaDeCarregador`, de `@nxvolt/shared`, e
 *      NENHUMA tela redeclara nada — publicar o tipo UMA VEZ é o que torna a
 *      divergência não-representável;
 *   2. o que o `tsc` continua sem ver (o SIGNIFICADO de `ageSeconds` assinado,
 *      de `means`, de `sharedBy`, de `labelIsIdentifying`) vira função nomeada
 *      aqui, com teste — `chargers-contrato.test.ts`.
 *
 * ---------------------------------------------------------------------------
 * ESTE MÓDULO NÃO CRIA VOCABULÁRIO PARALELO.
 *
 * O critério de ausência × vencimento × carimbo futuro × impossibilidade já
 * existe UMA vez: `@nxvolt/shared/frescor` (os predicados) e
 * `components/fleet/aggregates.ts` (a apresentação deles — `TRATAMENTO_DO_CAMPO`,
 * `FRASE_DO_CAMPO`, `estadoDeValor`, `textoDaIdadeDaLeitura`). Aqui se COMPÕE
 * aquilo para um segundo sujeito (o carregador), exatamente como `frescor.ts`
 * manda fazer no comentário de `FonteDeCarregador.contact`: *"É o vocabulário
 * `ausente` × `vencida` × `não-suportado` aplicado a um SEGUNDO SUJEITO, não um
 * vocabulário paralelo. NÃO criar predicado próprio para fonte."*
 *
 * ACOPLAMENTO ENTRE DOMÍNIOS, declarado: esta é uma tela de OCPP importando do
 * módulo de FROTA. O que se importa são as portas de BAIXO de `aggregates.ts` —
 * `estadoDeValor`, `TRATAMENTO_DO_CAMPO`, `FRASE_DO_CAMPO`,
 * `textoDaIdadeDaLeitura`, `listar`, `maiuscula` — que recebem a forma mínima e
 * não sabem nada de ônibus. É o mesmo registro que `ChargersPage` e
 * `modules-extra` já fazem para `valorDeParcela`, e o lugar certo delas é um
 * módulo comum (`lib/`) no dia em que isto for refatorado. Backlog NOMEADO.
 *
 * NADA AQUI CONHECE React, DOM OU `fetch`.
 */

import {
  ehCapacidadeDeEscopo,
  estadoDeLeitura,
  fonteNaoFornece,
  leituraDeIdade,
  type ComLeitura,
  type ConectorDeCarregador,
  type ConfiguracaoDeFrescorDeCarregador,
  type ContatoDeCarregador,
  type EstadoDeLeitura,
  type FonteDeCarregador,
  type LeituraDatada,
  type LinhaDeCarregador,
  type LinhaDeCarregadorAoVivo,
  type LinhaDeCarregadorDoMural,
  type MeioDeConexao,
  type MeioDeContato,
  type RespostaDeAprovacoes,
  type RespostaDeCarregador,
  type RespostaDeCarregadores,
  type RespostaDeMonitorAoVivo,
  type RespostaDePainelWall,
  type SessaoAtivaDeCarregador,
  type UnsupportedChargerField,
} from '@nxvolt/shared';
import {
  configuracaoDeFrota,
  idadeFormatadaDaLeitura,
  listar,
  textoDaIdadeDaLeitura,
  TRATAMENTO_DA_LEITURA,
  type ConfiguracaoDeFrota,
} from '../components/fleet/aggregates.js';
/* O apelido de EXIBIÇÃO do fabricante. Mora em `chargers-rotulos.ts` com o
   registro do custo (log diz `telluspower`, tela diz `Nansen`); aqui ele é
   embrulhado em `fabricanteEModelo` para as cinco telas escreverem igual. */
import { apelidoDeFabricante, temApelido } from './chargers-rotulos.js';

/**
 * Os tipos do contrato, REEXPORTADOS — e a razão é de ergonomia, não de gosto.
 *
 * Com um import só, a tela não tem motivo para "declarar ali mesmo" a forma que
 * lhe falta. A definição primária continua sendo UMA, a de `@nxvolt/shared`:
 * aqui não se redefine nada, só se repassa.
 */
export type {
  ConectorDeCarregador,
  ConfiguracaoDeFrescorDeCarregador,
  ContatoDeCarregador,
  FonteDeCarregador,
  LinhaDeCarregador,
  LinhaDeCarregadorAoVivo,
  LinhaDeCarregadorDoMural,
  MeioDeConexao,
  MeioDeContato,
  RespostaDeAprovacoes,
  RespostaDeCarregador,
  RespostaDeCarregadores,
  RespostaDeMonitorAoVivo,
  RespostaDePainelWall,
  SessaoAtivaDeCarregador,
  UnsupportedChargerField,
};

/* ===========================================================================
 * 1. O LIMIAR — vem do SERVIDOR, e só de lá
 * ========================================================================= */

/**
 * A configuração de frescor, a partir do `configuration.staleAfterSeconds` do
 * envelope.
 *
 * `simulated: false` porque não existe simulador de carregador neste projeto —
 * mesma situação da frota, e por isso `ConfiguracaoDeFrota` serve inteira: é
 * `FreshnessCfg` de `@nxvolt/shared` com outro nome.
 *
 * **NÃO EXISTE DEFAULT AQUI, de propósito.** Hardcodar 900 no cliente seria a
 * quinta grafia do mesmo limiar nesta base — `carregadores.ts` diz isso por
 * extenso. Quem não recebe o número não classifica frescor; diz o que sabe.
 */
export type ConfiguracaoDeCarregadores = ConfiguracaoDeFrota;

export function configuracaoDeCarregadores(staleAfterSeconds: number): ConfiguracaoDeCarregadores {
  return configuracaoDeFrota(staleAfterSeconds);
}

/* ===========================================================================
 * 2. O CONTATO — `ageSeconds` assinado, `means`, `sharedBy`
 * ========================================================================= */

/**
 * O contato na forma MÍNIMA que `frescor.ts` consome.
 *
 * `leituraDeIdade` é a fronteira onde `null` vira `latest: null` em vez de
 * virar `ageSeconds: 0` — que seria o clamp de novo, com outro nome. E o valor
 * NEGATIVO atravessa intacto: é ele que distingue "carimbo à frente do relógio"
 * de "o mais fresco possível".
 */
function leituraDoContato(contact: ContatoDeCarregador): ComLeitura<LeituraDatada> {
  return { latest: leituraDeIdade(contact.ageSeconds) };
}

/**
 * O estado do contato, pelo critério ÚNICO.
 *
 * **NÃO É EXPORTADO, e a ausência de `export` é a guarda.** Ver o cabeçalho de
 * `ContatoApresentado`: enquanto `EstadoDeLeitura` estava ao alcance da tela,
 * uma quinta cópia do predicado nasceu com `!== 'corrente'` e atravessou as
 * três guardas de teste. Tipo que a tela não alcança é decisão que a tela não
 * pode tomar.
 */
function estadoDoContato(
  contact: ContatoDeCarregador,
  cfg: ConfiguracaoDeCarregadores,
): EstadoDeLeitura {
  return estadoDeLeitura(leituraDoContato(contact), cfg);
}

/**
 * O limiar do servidor CHEGOU? `staleAfterSeconds` ausente vem como `0`, e com
 * zero QUALQUER idade positiva classifica como vencida — a tela inteira
 * vestiria o chrome de "sem sinal" por falta de um número.
 *
 * Esta checagem morava como `temLimiar` repetido em três telas. Mora aqui.
 */
function limiarUtilizavel(cfg: ConfiguracaoDeCarregadores): boolean {
  return Number.isFinite(cfg.staleAfterSeconds) && cfg.staleAfterSeconds > 0;
}

/**
 * **O PREDICADO DE SINAL. UM, PARA AS TRÊS TELAS.**
 *
 * ---------------------------------------------------------------------------
 * ELE NASCEU TRÊS VEZES, DIVERGENTES, DENTRO DO CONSERTO QUE EXISTIA PARA
 * IMPEDIR ISSO.
 *
 * `ChargersPage` tinha `contatoNaoSustentaDe` (sem `!connected`), e
 * `modules-extra` tinha `semSinalDe` escrito INLINE duas vezes, com corpos
 * idênticos entre si e diferentes do primeiro. **Nenhuma das três cobria
 * `'sem-leitura'`** — a AUSÊNCIA, que é o caso-assinatura desta frente inteira.
 *
 * O custo, medido ao vivo em `/app/chargers` com dados reais:
 *
 *     subtítulo : "19 carregadores, 1 com contato corrente, 17 sem sinal"
 *                  → 1 + 17 = 18, e são 19
 *     FZ043     : connected=false, contact.ageSeconds=null — NUNCA reportou
 *                  não entrava em balde nenhum, e o card dele estava
 *                  degradado com "(último)" duas dobras abaixo
 *
 * É o mesmo defeito do balde faltante que o comentário do subtítulo declarava
 * ter consertado, de volta com `sem-leitura` no lugar de `vencida`.
 *
 * OS QUATRO ESTADOS QUE SIGNIFICAM "NÃO HÁ SINAL VIVO", e por que os quatro:
 *
 *   `!connected`   o link OCPP caiu, ou a origem marcou o equipamento fora.
 *   `sem-leitura`  nunca reportou — não há snapshot a degradar nem a confiar.
 *   `futura`       o carimbo não é situável no tempo.
 *   `vencida`      respondeu e envelheceu além do limiar DO SERVIDOR.
 *
 * O limiar só é exigido para `vencida`: os outros três se decidem sem ele, e é
 * por isso que `limiarUtilizavel` guarda UMA linha e não a função inteira.
 *
 * A PARTIÇÃO É TOTAL por construção: `semSinalDe` e a sua negação cobrem o
 * conjunto, e `semSinal + comContatoCorrente === total` é a igualdade que o
 * teste varre. É ela que impede o balde faltante de voltar numa quarta forma.
 */
export function semSinalDe(
  c: Pick<LinhaDeCarregador, 'connected' | 'contact'>,
  cfg: ConfiguracaoDeCarregadores,
): boolean {
  return !c.connected || estadoEfetivo(c.contact, cfg) !== 'corrente';
}

/**
 * O ESTADO **EFETIVO** — o estado já coado pelo limiar, e a raiz desta rodada.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ELE EXISTE, E POR QUE ESCONDER CAMPOS NÃO BASTAVA.
 *
 * Remover `ContatoApresentado.estado` fechou a porta ÓBVIA e deixou abertas
 * todas as outras: qualquer campo derivado cuja PARTIÇÃO seja isomorfa à do
 * estado reconstrói a decisão sem precisar de import nenhum. Medido:
 *
 *     const isStale = !c.connected || contato.tom !== 'vivo';
 *
 * `TRATAMENTO_DA_LEITURA` mapeia 5 estados em 4 tons, e `vivo` é classe
 * UNITÁRIA de `corrente` — logo `tom === 'vivo'` ⟺ `estado === 'corrente'`,
 * publicado por desenho. `tsc` limpo, 947/947 verde, as três guardas cegas. E o
 * dano era o mesmo: `limiarUtilizavel` descartado, e com `staleAfterSeconds: 0`
 * a tela inteira vestindo o chrome de sem-sinal.
 *
 * Acrescentar `tom` à lista de proibidos cairia de novo na rodada seguinte com
 * `rotulo`, com `degradado`, com o próximo campo derivado.
 *
 * ---------------------------------------------------------------------------
 * O CONSERTO ESTRUTURAL: **UM estado, e todo campo publicado é função dele.**
 *
 * `estadoEfetivo` aplica `limiarUtilizavel` UMA vez, no lugar onde o estado
 * nasce. `vencida` sem limiar do servidor não é vencida — é `corrente`, porque
 * não há com o que comparar a idade. A partir daqui `semSinal`, `paleta`,
 * `degradado`, `rotuloDeSemSinal`, a frase e a legenda saem TODOS do mesmo
 * estado, e qualquer inversão que alguém faça sobre qualquer um deles converge
 * para a MESMA decisão em vez de divergir dela.
 *
 * Não é "mais um campo escondido": é a propriedade de que não há campo
 * publicado capaz de discordar de `semSinalDe`.
 */
function estadoEfetivo(
  contact: ContatoDeCarregador,
  cfg: ConfiguracaoDeCarregadores,
): EstadoDeLeitura {
  const estado = estadoDoContato(contact, cfg);
  /* Sem o limiar do servidor não há vencimento a declarar — e inventar um
     limiar local é a quinta grafia que este módulo recusa. */
  return estado === 'vencida' && !limiarUtilizavel(cfg) ? 'corrente' : estado;
}

/**
 * O RÓTULO do estado sem sinal — não-cromático e NOMEANDO A CAUSA.
 *
 * `Record` exaustivo sobre `EstadoDeLeitura` porque as causas pedem palavras
 * diferentes, e trocá-las por um "offline" genérico foi o que fez a MESMA
 * máquina (`FZ043`) sair **"Offline"** em `/app/chargers` e **"sem sinal"** no
 * Mural. Ausência não é vencimento: quem nunca reportou não está "sem sinal",
 * está sem nunca ter tido.
 *
 * EXPORTADO para que a guarda de DOM DERIVE os rótulos em vez de manter uma
 * cópia à mão. A superfície pública cresce um `Record` congelado; a alternativa
 * era uma lista de quatro strings no teste, que é a superfície escrita à mão
 * que o cabeçalho de `render-frota.tsx` condena e que já furou cinco vezes
 * nesta base.
 */
export const ROTULO_DE_SEM_SINAL: Record<EstadoDeLeitura, string> = {
  neutra: 'sem conexão',
  'sem-leitura': 'nunca reportou',
  futura: 'carimbo à frente',
  vencida: 'sem sinal',
  /* Alcançável: contato CORRENTE com `connected = false` — a fonte responde
     por ele e marca o equipamento fora. A causa é a conexão, não o carimbo. */
  corrente: 'sem conexão',
};

/**
 * O rótulo da causa. **`null` quando HÁ sinal**, e a nulidade é deliberada.
 *
 * Ele devolvia sempre uma string, e aí `rotulo === 'sem sinal'` era
 * `estado === 'vencida'` — ou seja `estaVencido`, que está na lista de imports
 * proibidos, reconstruído a partir de um export que precisa continuar público.
 * Pior: sem passar por `limiarUtilizavel`, divergia de `semSinalDe` com
 * `staleAfterSeconds: 0`.
 *
 * Agora ele sai do estado EFETIVO e compartilha a fronteira de `semSinalDe`:
 * `rotulo !== null` É a decisão, não uma reconstrução dela. Distinguir "nunca
 * reportou" de "sem sinal" continua possível — e isso é informação sobre a
 * CAUSA, que a tela deve mesmo ter.
 */
export function rotuloDeSemSinal(
  c: Pick<LinhaDeCarregador, 'connected' | 'contact'>,
  cfg: ConfiguracaoDeCarregadores,
): string | null {
  if (!semSinalDe(c, cfg)) return null;
  return ROTULO_DE_SEM_SINAL[estadoEfetivo(c.contact, cfg)];
}

/**
 * O que o carimbo MEDE, em palavras — e os dois NÃO são sinônimos.
 *
 * `charger_heartbeat` é o EQUIPAMENTO falando com este portal.
 * `source_poll` é ESTE PORTAL perguntando à integração de origem; o equipamento
 * pode estar morto há semanas e o carimbo continuar fresco. Escrever
 * "último heartbeat" sobre um poll é a família de defeito desta fase, e foi o
 * que o rodapé do card de carregador fazia para os 16 do VGPL.
 */
export const ROTULO_DO_MEIO_DE_CONTATO: Record<
  MeioDeContato,
  {
    curto: string;
    /**
     * O AGENTE da frase, já com a fonte NOMEADA — e é por isso que ele é função
     * e não string: "a fonte" no singular sobre duas fontes é a imprecisão que
     * gera o próximo chamado, e o operador precisa saber de quem se fala.
     */
    agente: (fonte: string) => string;
    ajuda: string;
  }
> = {
  charger_heartbeat: {
    curto: 'Último heartbeat',
    agente: () => 'heartbeat do próprio equipamento',
    ajuda: 'O próprio equipamento reportou a este portal por heartbeat OCPP.',
  },
  source_poll: {
    /* Rótulo VISÍVEL neutro para a vitrine: "Última resposta da fonte" vazava a
       ideia de um intermediário respondendo pelo equipamento. "Atualizado" diz
       só o frescor; a procedência honesta (poll da origem) segue no `agente` e
       no `ajuda`, que não são mais renderizados nos cards. */
    curto: 'Atualizado',
    agente: (fonte) => `resposta de ${fonte}, não do equipamento`,
    ajuda:
      'O carimbo é do POLL da integração de origem, não do equipamento. ' +
      'A fonte responder não prova que o carregador está vivo.',
  },
};

/**
 * O que `connected` AFIRMA — mesmo nome, duas afirmações, e a colisão JÁ ESTÁ
 * em produção dentro da view. A coluna não é renomeada; o significado é DITO.
 */
export const ROTULO_DO_MEIO_DE_CONEXAO: Record<
  MeioDeConexao,
  { curto: string; frase: string }
> = {
  ocpp_link: {
    curto: 'link OCPP',
    frase: 'Há WebSocket OCPP aberto entre o equipamento e este portal.',
  },
  source_status: {
    curto: 'status na origem',
    frase:
      'É o estado que o portal de origem mantém para este equipamento. ' +
      'Não é uma conexão com este portal.',
  },
};

/** O contato pronto para a célula: estado, degradação, rótulo e as frases. */
export interface ContatoApresentado {
  /*
    `estado: EstadoDeLeitura` SAIU DAQUI, e a remoção é o conserto.

    Com ele publicado, a tela tinha o literal ao alcance e podia reescrever a
    decisão de sinal — medido: `const isStale = !c.connected ||
    contato.estado !== 'corrente';` compilava limpo e passava pelas TRÊS
    guardas (tipo, varredura de fonte e DOM), descartando `limiarUtilizavel` e
    fazendo a tela inteira vestir o chrome de sem-sinal com
    `staleAfterSeconds: 0`.

    A varredura de fonte não podia pegar: são os mesmos literais, no mesmo
    arquivo, só com o operador complementar — e `ManobristaPage` já escreve
    `!== "corrente"` neste repositório, então a forma não é hipotética.

    O que a tela precisa, ela tem: `tom` para a paleta, `rotulo` e `frase` para
    o texto, `degradado` para o chrome. Quem precisa decidir "há sinal?" chama
    `semSinalDe`. Quem precisa do rótulo da causa chama `rotuloDeSemSinal`.
  */
  /**
   * A DECISÃO, publicada — e não a classificação de onde ela sai.
   *
   * É `semSinalDe(c, cfg)`, com `limiarUtilizavel` dentro. A tela lê isto; não
   * recompõe a partir de campo derivado.
   */
  semSinal: boolean;
  /**
   * A PALETA já resolvida. `null` quando há sinal — e a nulidade é a mesma
   * fronteira de `semSinal`, de propósito: qualquer inversão converge para a
   * decisão em vez de divergir dela.
   *
   * Substitui o `tom`, que era a fuga desta rodada: `TRATAMENTO_DA_LEITURA`
   * mapeia 5 estados em 4 tons com `vivo` como classe UNITÁRIA de `corrente`,
   * então `tom === 'vivo'` era `estado === 'corrente'` publicado por desenho.
   */
  paleta: 'faulted' | 'offline' | null;
  /**
   * O rótulo da causa do sem-sinal, pronto. `null` quando há sinal — MESMA
   * fronteira de `semSinal`, para que a inversão convirja em vez de divergir.
   */
  rotuloSemSinal: string | null;
  /** Aplica a degradação do padrão STALE no valor. */
  degradado: boolean;
  /** O rótulo do CAMPO, já discriminado por `means`. Portador não-cromático. */
  rotulo: string;
  /** A frase de idade, pronta — a tela nunca vê `ageSeconds`. */
  frase: string;
  /** Ressalva de compartilhamento de carimbo. `null` quando não há o que dizer. */
  fraseDeCompartilhamento: string | null;
  /** O que `connected` afirma para esta fonte. */
  fraseDeConexao: string;
}

/**
 * O contato de UM carregador, pronto.
 *
 * `formatarIdade` recebe o valor ASSINADO e imprime o sinal — é `fmtAge` de
 * `components/substation/bands.ts`, o mesmo que a frota usa, e o cabeçalho dele
 * registra por extenso por que não há `Math.max(0, …)` lá dentro.
 *
 * Objeto pronto, e não campos soltos, pelo mesmo motivo que `celulaDeCampo` da
 * frota: com estado, valor e frase soltos a revisão recompôs o valor a partir do
 * dado cru três vezes — e o resultado é pior que o defeito original, porque a
 * tela passa a ATESTAR uma procedência que o número não tem.
 */
export function contatoApresentado(
  /*
    `connected` ENTROU no `Pick`, e a falta dele era um defeito que eu mesmo
    acabara de criar ao publicar `semSinal` aqui: sem `connected`, o objeto
    diria `semSinal: false` para um equipamento com contato corrente que a
    fonte marca FORA, enquanto `semSinalDe` diria `true`. Publicar a decisão e
    calculá-la com menos informação do que o predicado é trocar uma divergência
    de lugar — exatamente o que esta rodada existe para encerrar.
  */
  c: Pick<LinhaDeCarregador, 'connected' | 'contact' | 'sourceLabel' | 'connectedMeans'>,
  cfg: ConfiguracaoDeCarregadores,
  formatarIdade: (segundos: number) => string,
): ContatoApresentado {
  const leitura = leituraDoContato(c.contact);
  /* TODO campo abaixo sai do estado EFETIVO — ver `estadoEfetivo`. É isso que
     impede um derivado de discordar de `semSinal`. */
  const estado = estadoEfetivo(c.contact, cfg);
  const tratamento = TRATAMENTO_DA_LEITURA[estado];
  const meio = ROTULO_DO_MEIO_DE_CONTATO[c.contact.means];
  /* A MESMA função que a tela chamaria — não uma reconstrução dela. */
  const semSinal = semSinalDe(c, cfg);
  return {
    semSinal,
    /* `perigo` é o carimbo à frente (o dado aparenta ser o mais fresco de
       todos e não é situável no tempo); os demais sem-sinal vão para a paleta
       neutra. Com sinal, `null`. */
    paleta: !semSinal ? null : tratamento.tom === 'perigo' ? 'faulted' : 'offline',
    rotuloSemSinal: semSinal ? ROTULO_DE_SEM_SINAL[estado] : null,
    degradado: tratamento.degradado,
    rotulo: meio.curto,
    frase: FRASE_DO_CONTATO[estado]({
      idade: leitura.latest === null ? '—' : formatarIdade(leitura.latest.ageSeconds),
      fonte: c.sourceLabel,
      agente: meio.agente(c.sourceLabel),
    }),
    fraseDeCompartilhamento: fraseDeCarimboCompartilhado(c.contact, c.sourceLabel),
    fraseDeConexao: ROTULO_DO_MEIO_DE_CONEXAO[c.connectedMeans].frase,
  };
}

interface ContextoDoContato {
  idade: string;
  fonte: string;
  /** O agente, já com a fonte nomeada. Ver `ROTULO_DO_MEIO_DE_CONTATO`. */
  agente: string;
}

/**
 * Uma frase por estado, `Record` exaustivo — e NENHUMA delas diz "heartbeat"
 * por conta própria: quem decide a palavra é `means`, via `sujeito`.
 *
 * Sem "ainda" em nenhuma, pela razão de sempre: o advérbio promete que esperar
 * resolve.
 */
const FRASE_DO_CONTATO: Record<EstadoDeLeitura, (c: ContextoDoContato) => string> = {
  /* Inalcançável sem simulador de carregador. Fica escrito porque `Record`
     exaustivo cobra, e porque em simulado não se afirma vivacidade. */
  neutra: () => 'Dado de demonstração — sem afirmação de vivacidade.',
  'sem-leitura': (c) => `Sem carimbo de contato — ${c.fonte} nunca reportou este equipamento.`,
  /* NÃO "há 0 s": a leitura não está fresca, está fora de hora. A frase nomeia
     a causa provável porque é o que permite agir. */
  futura: (c) =>
    `Carimbo de tempo ${c.idade}, à frente do relógio do portal: o contato não pode ser ` +
    `situado no tempo. Verifique o fuso da sincronia com ${c.fonte} e o relógio de origem.`,
  vencida: (c) => `Sem contato há ${c.idade} — o último veio por ${c.agente}.`,
  corrente: (c) => `Contato há ${c.idade}, por ${c.agente}.`,
};

/**
 * A LEGENDA DO BLOCO DE CONECTORES DEGRADADO — e ela nomeia a CAUSA.
 *
 * Era uma frase só, "último snapshot antes da desconexão", para qualquer card
 * degradado. Medido ao vivo: ela aparecia sobre um carregador com carimbo 3 h NO
 * FUTURO (que não está desconectado) e sobre um que NUNCA reportou (para o qual
 * não existe "antes da desconexão"). Afirmar uma desconexão que não aconteceu é
 * a mesma família de defeito que esta fase persegue, no texto da ressalva.
 *
 * `null` quando não há degradação: uma ressalva permanente treina o operador a
 * ignorá-la.
 */
export function legendaDeConectoresDegradados(
  c: Pick<LinhaDeCarregador, 'connected' | 'contact'>,
  cfg: ConfiguracaoDeCarregadores,
): string | null {
  /*
    SAI DO ESTADO EFETIVO e da MESMA decisão — `null` exatamente quando há
    sinal. Com `estadoDoContato` cru, um `vencida` sem limiar do servidor
    produzia legenda de degradação sobre um card que `semSinalDe` considera
    vivo: mais um campo publicado capaz de discordar da decisão.
  */
  if (!semSinalDe(c, cfg)) return null;
  const estado = estadoEfetivo(c.contact, cfg);
  if (estado === 'futura') {
    return 'estados não situáveis no tempo — o carimbo do contato está à frente do relógio';
  }
  if (estado === 'vencida') return 'último estado antes de a fonte parar de responder';
  if (estado === 'sem-leitura') {
    /* Alcançável com `connected: true`: a fonte responde e nunca reportou ESTE
       equipamento. O dublê isola este caso desde que cada linha passou a provar
       um braço só — antes ele vinha sempre junto de `!connected` e a frase
       abaixo o cobria por acidente. */
    return 'sem carimbo de contato — estes são os últimos estados que a fonte entregou';
  }
  return 'último estado antes da desconexão';
}

/**
 * A RESSALVA DE CARIMBO COMPARTILHADO — e ela vale nos DOIS sentidos.
 *
 * `sharedBy > 1` significa que N carregadores VISÍVEIS DA MESMA FONTE carregam
 * EXATAMENTE o mesmo carimbo. Medido em 30/09/2026:
 *
 *   - 17 Nansen `GP1` compartilham UM carimbo, ao segundo, sempre FRESCO.
 *     Apresentá-los como "17 carregadores com heartbeat de 12 s" é afirmar
 *     falsidade dezessete vezes — e é pior que um campo vazio, porque é
 *     exatamente o que o operador espera ver;
 *   - 16 Nansen `GP3 Pátio` pararam TODOS em `2026-09-11 13:23:54`, no MESMO
 *     SEGUNDO. Isso é UMA ponte caída, não 16 falhas independentes.
 *
 * Por isso a ressalva NÃO é condicionada ao vencimento: declarar só o vencido
 * deixaria o caso simétrico — o compartilhamento FRESCO — exatamente como
 * estava. É MEDIÇÃO e não inferência: não adivinha causa, não olha `vendor`,
 * não usa prefixo de `cpId`.
 *
 * `1` é carimbo PRÓPRIO e `null` é ausência de carimbo; nos dois não há ressalva
 * a fazer, e uma ressalva permanente treina o operador a ignorá-la.
 */
export function fraseDeCarimboCompartilhado(
  contact: ContatoDeCarregador,
  sourceLabel: string,
): string | null {
  const n = contact.sharedBy;
  if (n === null || !Number.isFinite(n) || n <= 1) return null;
  return (
    `${n} carregadores de ${sourceLabel} carregam exatamente este mesmo carimbo. ` +
    `É um carimbo da fonte, não ${n} sinais independentes de equipamento.`
  );
}

/* ===========================================================================
 * 3. O NOME EXIBIDO
 * ========================================================================= */

/**
 * O nome do carregador na tela. **Rótulo ausente NÃO vira nome exibido.**
 *
 * `labelIsIdentifying` é `false` quando o rótulo não acrescenta identidade:
 * vazio, ou igual (normalizado) a `vendor`, a `model` ou a `vendor + ' ' + model`.
 * Medido: dois `sx` têm `label = 'sx PointModel'`, que é o fabricante e o modelo
 * — não um nome que alguém deu ao equipamento.
 *
 * O critério é calculado NO BANCO (`charger_visible.label_is_identifying`), uma
 * vez. Aqui só se consome.
 *
 * `cpId` é a escolha deliberada do fallback: é a CHAVE NATURAL, o que aparece em
 * log e em URL de suporte. O `label != null` extra NÃO é redundante — ele é a
 * rede contra um envelope parcial em que `labelIsIdentifying` venha `true` com
 * `label` nulo, e é exatamente a forma de `undefined` que já derrubou o portal
 * inteiro nesta base.
 */
export function nomeDoCarregador(
  c: Pick<LinhaDeCarregador, 'cpId' | 'label' | 'labelIsIdentifying'>,
): string {
  if (c.labelIsIdentifying !== true) return c.cpId;
  const rotulo = typeof c.label === 'string' ? c.label.trim() : '';
  return rotulo.length > 0 ? rotulo : c.cpId;
}

/**
 * FABRICANTE E MODELO COMO A TELA ESCREVE — em UM lugar, para as cinco telas.
 *
 * O apelido de exibição (`telluspower` → `Nansen`) vivia aplicado em UMA tela.
 * Medido pela revisão cruzada: `/app/chargers` imprimia `Nansen`, enquanto o
 * Mural, o Detalhe e a fila de Aprovação imprimiam `telluspower` nos MESMOS
 * equipamentos. O apelido existe para o operador reconhecer o fornecedor pelo
 * nome que ele usa; metade do portal contradizendo a outra metade sobre o nome
 * do fabricante é pior que não ter apelido nenhum.
 *
 * `titulo` carrega o valor do cadastro e do log, e é o único lugar onde ele
 * aparece — o custo registrado em `chargers-rotulos.ts` continua valendo: log e
 * suporte dizem `telluspower`, a tela diz `Nansen`.
 *
 * `model` vem `''` (string vazia) nos equipamentos sincronizados, e `??` não
 * cobre string vazia: sem o `trim() || null` a célula saía "Nansen · " com o
 * segundo termo em branco.
 */
export function fabricanteEModelo(
  c: Pick<LinhaDeCarregador, 'vendor' | 'model'>,
): { texto: string; titulo: string | undefined } {
  const partes = [apelidoDeFabricante(c.vendor), (c.model ?? '').trim() || null].filter(
    (x): x is string => typeof x === 'string' && x.length > 0,
  );
  return {
    texto: partes.length === 0 ? 'não informado' : partes.join(' · '),
    titulo: temApelido(c.vendor) ? `Fabricante no cadastro e no log: ${c.vendor}` : undefined,
  };
}

/* ===========================================================================
 * 4. CAPACIDADE DA FONTE — e o 409 que ela produz
 * ========================================================================= */

/**
 * A fonte deste carregador FORNECE esta capacidade?
 *
 * Delega a `fonteNaoFornece` de `@nxvolt/shared/frescor`, que é o único lugar do
 * projeto que interpreta `unsupported_fields`. Uma segunda leitura da lista é
 * uma segunda verdade.
 */
export function fonteForneceCapacidade(
  c: Pick<LinhaDeCarregador, 'unsupportedFields'>,
  campo: UnsupportedChargerField,
): boolean {
  return !fonteNaoFornece(c.unsupportedFields, campo);
}

/**
 * Por que os comandos remotos estão indisponíveis. `null` quando estão.
 *
 * Os quatro comandos (start, stop, reset, unlock) estão em UMA capacidade —
 * `remote_control` — porque todos dependem da MESMA coisa: haver WebSocket OCPP
 * com ESTE portal.
 *
 * **OS BOTÕES NÃO SÃO RENDERIZADOS**, e não "nascem desabilitados" como esta
 * frase dizia: botão `disabled` sai da ordem de tabulação e leva a causa só no
 * `title`, que aparece no hover. Medido: 32 botões cinza sem razão para teclado
 * e leitor de tela (4.1.2). Quem não pode ser acionado nunca não é um controle
 * desabilitado — a frase ocupa o lugar dele.
 */
export function motivoDeComandoIndisponivel(
  c: Pick<LinhaDeCarregador, 'unsupportedFields' | 'sourceLabel'>,
): string | null {
  if (fonteForneceCapacidade(c, 'remote_control')) return null;
  return (
    `Comandos remotos exigem WebSocket OCPP com este portal. Os carregadores de ` +
    `${c.sourceLabel} falam OCPP com o servidor de origem, e não há caminho para ` +
    `enviar o comando.`
  );
}

/** O corpo do 409 `SOURCE_UNSUPPORTED`, como o servidor o emite. */
export interface FonteNaoSuportada {
  error: 'source_unsupported';
  code: 'SOURCE_UNSUPPORTED';
  capability: UnsupportedChargerField | 'garage_assignment';
  source: string;
  sourceLabel: string;
  cpId: string;
  /** Frase PRONTA, do servidor. A tela pode encurtar; não pode INVENTAR. */
  message: string;
}

/**
 * Reconhece o 409 de impossibilidade de fonte num corpo de resposta qualquer.
 *
 * Exige `code` E `message`: `code` sozinho identificaria a família, mas sem a
 * frase a tela não tem o que dizer e cairia num texto inventado — que é
 * justamente o que o servidor existe para impedir aqui, porque a afirmação é
 * sobre a TOPOLOGIA DE FONTES e quem a conhece é ele.
 */
export function erroDeFonteNaoSuportada(corpo: unknown): FonteNaoSuportada | null {
  if (corpo === null || typeof corpo !== 'object') return null;
  const o = corpo as Record<string, unknown>;
  if (o.code !== 'SOURCE_UNSUPPORTED') return null;
  if (typeof o.message !== 'string' || o.message.trim().length === 0) return null;
  return o as unknown as FonteNaoSuportada;
}

/**
 * A frase de uma falha de comando — e o 409 NÃO é tratado como erro genérico.
 *
 * As três famílias dizem coisas diferentes e pedem ações diferentes:
 *
 *   409  IMPOSSIBILIDADE PERMANENTE da fonte. Não é falha do portal nem recusa
 *        do equipamento. A frase já vem pronta do servidor.
 *   503  TRANSITÓRIO — a fonte FORNECE o comando e a tentativa falhou (socket
 *        fechado, timeout do CP). "Tente de novo" é verdade aqui, e só aqui.
 *   resto erro de verdade.
 *
 * Com `alert("Erro 409")` o operador lê "o portal quebrou" sobre uma
 * incompatibilidade permanente — e abre chamado para um equipamento que não tem
 * defeito nenhum.
 */
export function fraseDeFalhaDeComando(status: number, corpo: unknown): string {
  const impossivel = erroDeFonteNaoSuportada(corpo);
  if (impossivel !== null) return impossivel.message;
  const o = corpo !== null && typeof corpo === 'object' ? (corpo as Record<string, unknown>) : {};
  const detalhe = typeof o.error === 'string' ? o.error : null;
  if (status === 503) {
    return (
      `O portal não conseguiu falar com o equipamento agora` +
      `${detalhe === null ? '' : ` (${detalhe})`}. Tente novamente em alguns instantes.`
    );
  }
  if (status === 403) return 'Seu perfil não tem permissão para operar este comando.';
  if (status === 404) return 'Este carregador não está visível para o seu acesso.';
  return `Não foi possível executar o comando${detalhe === null ? '' : `: ${detalhe}`}.`;
}

/* ===========================================================================
 * 5. AS QUATRO RELAÇÕES VAZIAS — dois vocabulários que NÃO se misturam
 * ========================================================================= */

/**
 * Por que a relação está vazia. **As duas causas pedem frases diferentes.**
 *
 * `impossibilidade` — não há caminho, não há integração pendente, não há espera
 *                     que resolva. `ocpp_messages` e `meter_values` só têm
 *                     escritor em `ocpp.ts`, e os carregadores exibidos falam
 *                     OCPP com o servidor de ORIGEM.
 * `escopo`          — a capacidade de sincronizar existe e foi deixada fora
 *                     DESTA VERSÃO. Quem resolve é uma versão, não o tempo.
 *
 * Fundir as duas num estado vazio genérico treina o operador a ignorar as duas —
 * e é o que o cabeçalho de `campos-carregador.ts` proíbe por extenso.
 */
export type CausaDeRelacaoVazia = 'impossibilidade' | 'escopo';

/** As quatro relações que ficam vazias hoje, nomeadas pela VIEW que as serve. */
export type RelacaoDeCarregador =
  | 'ocpp_message_visible'
  | 'transaction_visible'
  | 'alert_visible'
  | 'maintenance_order_visible';

/**
 * A CAPACIDADE que cada view consome — e é por aqui que a classificação entra.
 *
 * Existe para que `VAZIO_DE_CARREGADOR[*].causa` **não seja escrita à mão**. Ela
 * era, e a revisão cruzada mediu o custo: `CAPACIDADES_DE_ESCOPO` e
 * `ehCapacidadeDeEscopo()` já estavam publicados em `@nxvolt/shared`, `apps/web`
 * não importava nenhum dos dois, e mover `alerts` para impossibilidade no shared
 * não produzia vermelho nenhum aqui. Duas verdades sobre a mesma pergunta é o
 * mecanismo que este módulo existe para fechar — e ele tinha reaberto.
 */
const CAPACIDADE_DA_RELACAO: Record<RelacaoDeCarregador, UnsupportedChargerField> = {
  ocpp_message_visible: 'ocpp_messages',
  transaction_visible: 'transactions',
  alert_visible: 'alerts',
  maintenance_order_visible: 'maintenance_orders',
};

/** A causa, DERIVADA do shared. Nenhuma tela e nenhum `Record` a reescreve. */
export function causaDeRelacaoVazia(relacao: RelacaoDeCarregador): CausaDeRelacaoVazia {
  return ehCapacidadeDeEscopo(CAPACIDADE_DA_RELACAO[relacao]) ? 'escopo' : 'impossibilidade';
}

/**
 * `Record` exaustivo: uma quinta relação não compila até ganhar a sua frase, em
 * vez de herdar um template que não serve para ela.
 *
 * Frase POR RELAÇÃO e não template com o nome interpolado, pela mesma razão que
 * `FRASE_DE_RECURSO` da frota registra: as grandezas têm gêneros diferentes
 * ("a mensagem", "a transação", "o alerta", "a ordem") e a concordância cai no
 * particípio também. Verboso, e a única forma que produz português correto nos
 * quatro casos — o texto É o produto destas telas.
 *
 * **`causa` NÃO MORA MAIS AQUI**: é um `getter` sobre `causaDeRelacaoVazia`, que
 * lê `ehCapacidadeDeEscopo` do shared. Mudar a partição lá muda a frase aqui.
 *
 * AS FRASES DE ESCOPO NÃO AFIRMAM MAIS EXISTÊNCIA. Elas diziam "existem na fonte
 * … o dado está lá", e a remedição de 31/10/2026 desmentiu metade:
 * `nxv_vgpl.alerts = 0` e `nxv_vgpl.maintenance_orders = 0` na fonte VGPL (as 73
 * eram `public.maintenance_orders`, do próprio nxvolt, lidas de volta por
 * auto-referência da view). A CLASSIFICAÇÃO continua escopo — o balde descreve a
 * relação entre portal e fonte, não a contagem de hoje —, mas afirmar que o dado
 * está lá quando há zero é inventar um fato para sustentar uma classificação que
 * não precisa dele.
 */
export const VAZIO_DE_CARREGADOR: Record<
  RelacaoDeCarregador,
  { readonly causa: CausaDeRelacaoVazia; titulo: string; frase: (fontes: string) => string }
> = {
  ocpp_message_visible: {
    get causa() {
      return causaDeRelacaoVazia('ocpp_message_visible');
    },
    titulo: 'Esta fonte não fala OCPP com este portal',
    frase: (fontes) =>
      `Esta tela lê o servidor OCPP deste portal. Os carregadores de ${fontes} falam OCPP ` +
      `com o servidor de origem, e as mensagens não passam por aqui: não há caminho e ` +
      `não existe mensagem a listar para eles.`,
  },
  transaction_visible: {
    get causa() {
      return causaDeRelacaoVazia('transaction_visible');
    },
    titulo: 'Fora do escopo desta versão',
    frase: (fontes) =>
      `As sessões de recarga de ${fontes} não são sincronizadas nesta versão do portal. ` +
      `A sincronia existe como capacidade e ficou fora desta versão — não é uma falha ` +
      `a reportar nem algo que esperar resolva.`,
  },
  alert_visible: {
    get causa() {
      return causaDeRelacaoVazia('alert_visible');
    },
    titulo: 'Fora do escopo desta versão',
    frase: (fontes) =>
      `Os alertas de ${fontes} não são sincronizados nesta versão do portal. Um alerta ` +
      `que apareça lá amanhã não chegaria aqui — a sincronia ficou fora desta versão.`,
  },
  maintenance_order_visible: {
    get causa() {
      return causaDeRelacaoVazia('maintenance_order_visible');
    },
    titulo: 'Fora do escopo desta versão',
    frase: (fontes) =>
      `O histórico de ordens de manutenção de ${fontes} não é sincronizado nesta versão ` +
      `do portal. Abrir ordem por este portal FUNCIONA para estes carregadores — o que ` +
      `não vem é o histórico de lá.`,
  },
};

/** A frase pronta, com as fontes NOMEADAS. Quem não nomeia gera o próximo ticket. */
export function fraseDeRelacaoVazia(
  relacao: RelacaoDeCarregador,
  rotulosDeFonte: readonly string[],
): string {
  const quem = rotulosDeFonte.length === 0 ? 'da fonte configurada' : listar(rotulosDeFonte);
  return VAZIO_DE_CARREGADOR[relacao].frase(quem);
}

/** As fontes (por rótulo, sem repetir) que não fornecem a capacidade. */
export function fontesSemCapacidade(
  fontes: readonly FonteDeCarregador[],
  campo: UnsupportedChargerField,
): string[] {
  return [
    ...new Set(fontes.filter((f) => fonteNaoFornece(f.unsupportedFields, campo)).map((f) => f.label)),
  ].sort();
}

/* ===========================================================================
 * 6. O ADAPTADOR DE LINHA CRUA **NÃO EXISTE MAIS** — e a ausência é o conserto.
 *
 * Havia aqui `LinhaCruaDeCarregador` + `linhaDeCarregadorDaView()`, para
 * converter o que `/api/wall` e `/api/live` emitiam (a linha crua de
 * `charger_visible`: `serial`, `unsupportedChargerFields`, `contact*` soltos).
 *
 * DUAS COISAS O MATARAM, e as duas são medição:
 *
 * 1. **O ADAPTADOR ERA CEGO.** Todos os campos de `LinhaCruaDeCarregador` eram
 *    `?: unknown` — um tipo assim aceita QUALQUER objeto, inclusive o certo. Não
 *    era verificação, era afirmação: a mesma cegueira de `r.json()` com outra
 *    roupa, dentro do arquivo criado para abolir cópias do tipo.
 *
 * 2. **O BACKEND CONSERTOU O LADO DELE.** `/api/wall` e `/api/live` passaram a
 *    emitir `RespostaDePainelWall` e `RespostaDeMonitorAoVivo`, com `contact`
 *    ANINHADO. Medido contra o servidor reiniciado a partir do código em disco
 *    em 31/10/2026:
 *
 *        chaves: chargers, sources, totalVisivel, generatedAt, configuration
 *        contact: { at, ageSeconds: 56, means: 'source_poll', sharedBy: 8 }
 *        'contactAt' in linha === false   ·   'serialNumber' in linha === true
 *
 *    Alimentado com a forma NOVA, o adaptador lia `bruta.contactAt` (ausente) e
 *    devolvia `contact: { at: null, ageSeconds: null, means: 'source_poll',
 *    sharedBy: null }` — e as duas telas diziam **"sem carimbo" em 100% dos
 *    cards**. Três falsidades de uma vez: ausência no lugar de contato de 56 s,
 *    `means` rebaixado de heartbeat para poll, e a ressalva de carimbo
 *    compartilhado (`sharedBy: 8`) sumindo. Era o `Último heartbeat: —`
 *    ressuscitado com outro texto.
 *
 * As telas agora tipam `useApi<RespostaDePainelWall>` e
 * `useApi<RespostaDeMonitorAoVivo>`. **Não recrie uma camada de tradução aqui**:
 * se a forma divergir de novo, o lugar do conserto é a rota.
 * ========================================================================= */

/* ===========================================================================
 * 7. O RECORTE DECLARÁVEL
 * ========================================================================= */

/**
 * A ressalva de truncamento. `null` quando não há recorte.
 *
 * `totalVisivel` × `amostrados` existe para o recorte ser DIZÍVEL: com o filtro
 * de aprovação e o gate de garagem aplicados os dois divergem, e aí a tela
 * precisa poder declarar que está vendo uma amostra — em vez de imprimir um
 * subtítulo sobre o total ao lado de uma grade sobre a amostra.
 *
 * Os dois `Number.isFinite` NÃO são defensividade decorativa: com um envelope
 * sem os campos (servidor antigo, resposta truncada) a comparação
 * `undefined >= undefined` é `false` e a frase saía
 * "Mostrando undefined de undefined" — foi o que o render do Mapa imprimiu, na
 * frase que existe para DECLARAR que o valor é parcial.
 */
export function ressalvaDeRecorteDeCarregadores(
  amostrados: number,
  totalVisivel: number,
): string | null {
  if (!Number.isFinite(amostrados) || !Number.isFinite(totalVisivel)) return null;
  if (amostrados >= totalVisivel) return null;
  return (
    `Mostrando ${amostrados} de ${totalVisivel} carregadores visíveis — a lista está ` +
    `recortada e os números do cabeçalho falam desta amostra.`
  );
}

/**
 * A frase de idade sobre a forma MÍNIMA — a porta de baixo, para quem já tem a
 * leitura e não a linha inteira (o bloco `contact` de uma FONTE, por exemplo).
 *
 * Reexporta o corpo da frota em vez de reimplementar: uma frase, duas portas,
 * zero reimplementações.
 */
export function textoDaIdadeDoContato(
  contact: ContatoDeCarregador,
  cfg: ConfiguracaoDeCarregadores,
  formatarIdade: (segundos: number) => string,
): string {
  return textoDaIdadeDaLeitura(leituraDoContato(contact), cfg, formatarIdade);
}

/**
 * A idade CURTA, para caber numa célula de KPI.
 *
 * Existe porque a frase completa de `contatoApresentado` tem 180 caracteres e,
 * posta num `nx-kpi__value`, quebrava uma palavra por linha num cartão de 300 px
 * de altura — medido na tela. A frase completa continua existindo e vai para a
 * faixa de contato, onde há largura; aqui fica o número.
 *
 * `idadeFormatadaDaLeitura` é a porta de baixo da frota, reusada: o `null` vira
 * travessão lá, não aqui, e `formatarIdade` recebe o valor ASSINADO.
 */
export function idadeCurtaDoContato(
  contact: ContatoDeCarregador,
  formatarIdade: (segundos: number) => string,
): string {
  const curta = idadeFormatadaDaLeitura(leituraDoContato(contact), formatarIdade);
  return curta === '—' ? 'sem carimbo' : curta;
}
